import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { debounce, get } from 'lodash';
import { AlertTriangle, Globe, Trash2 } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { SiteScrapeDiscardConfirm } from './SiteScrapeDiscardConfirm.dialog';
import { SiteScrapeUpgrade } from './SiteScrapeUpgrade';
import { UrlTree } from './UrlTree';
import {
  clearSelection,
  countReviewSelection,
  mergeChildren,
  rootDomain,
  seedSelection,
  SelectionSource,
  toggleSelection,
  trimToLimit,
  UrlTreeNode,
} from './selection';
import {
  clearSiteMapFlags,
  clearSiteMapSnooze,
  flagSiteMapResume,
  forgetSiteMap,
  isSiteMapEmpty,
  SITEMAP_CONSUMED_KEY,
  siteMapKey,
  siteMapResumeTarget,
  siteMapSnoozeState,
} from './siteMapFlags';
import { QueryKey } from '@/Config/api/queryKey';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { UploadItemTypeEnum } from '@/Domain/enum/UploadItemType.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import { useUploadStore } from '@/Domain/store/upload.store';
import { cn } from '@/Domain/utils/cn';
import {
  ONBOARDING_DISCOUNT_PERCENT_OFF,
  useOnboardingDiscount,
} from '@/UI/GettingStarted/components/onboardingDiscount';

type SiteMapStatus = 'none' | 'mapping' | 'ready' | 'failed';

interface SiteMapPreview {
  status: SiteMapStatus;
  total_urls: number;
  tree: UrlTreeNode | Record<string, never>;
  selection: string[];
  scrape_limit: number | null;
  articles_left: number | null;
  error_message: string | null;
  homepage_reject_reason: string | null;
}

// Backend `scrape_rejection_reason` verdict → advisory copy, mirroring
// LOW_QUALITY_COPY_KEY in the Getting Started flow. `reason` is untyped because
// it arrives straight off the site-map schema, so an unrecognised verdict falls
// back to the thin copy rather than rendering nothing.
const LOW_QUALITY_MODAL_KEY: Record<string, string> = {
  http_error: 'tabs.website.review.low_quality.blocked',
  error_page: 'tabs.website.review.low_quality.blocked',
  off_domain_redirect: 'tabs.website.review.low_quality.wrong_site',
  auth_wall: 'tabs.website.review.low_quality.auth_wall',
};

function lowQualityModalKey(reason: string): string {
  return LOW_QUALITY_MODAL_KEY[reason] ?? 'tabs.website.review.low_quality.thin';
}

const NO_LIMIT = Number.POSITIVE_INFINITY;

const SELECTION_CHANGED_DEBOUNCE_MS = 1200;

const SELECTION_SAVE_DEBOUNCE_MS = 800;

type SelectionAction = 'toggle' | 'fit' | 'deselect_all' | 'initial_fit';

type ReviewTrigger = 'auto' | 'dock';

interface PendingReviewOpen {
  totalUrls: number;
  totalPages: number;
  selectedCount: number;
  selectionSource: SelectionSource;
  wasSnoozed: boolean;
}

// The server reads `[]` as "never chosen", so an explicit empty selection is saved as an exclusion of the root.
const toSavedSelection = (selection: Set<string>, tree: UrlTreeNode): string[] =>
  selection.size > 0 ? Array.from(selection) : [`!${tree.path}`];

const readTrigger = (): ReviewTrigger | null => {
  const trigger = useModalStore.getState().params?.trigger;
  return trigger === 'auto' || trigger === 'dock' ? trigger : null;
};

// Centered review modal. The URL is submitted elsewhere (the Add-website modal's
// All Pages tab, or onboarding); the dock opens this when the map is ready.
export const SiteScrapeReviewModal = () => {
  const { t } = useTranslation(['knowledge_base']);
  const queryClient = useQueryClient();
  const open = useModalStore((store) => store.isOpen(MODAL_NAMES.SITE_SCRAPE_REVIEW_MODAL_ID));
  const closeModal = useModalStore((store) => store.closeModal);
  const { state: discount, isClaimable: discountClaimable } = useOnboardingDiscount();
  const { getConfig } = useSubscription();
  // Treat "still loading" as paid: the safe default routes plan changes through
  // the subscription page instead of an instant checkout.
  const isPaidPlan = getConfig('tier') !== 'free';
  const addItems = useUploadStore((store) => store.addItems);
  const { capture } = usePostHogAnalytics();

  const [selection, setSelection] = useState<Set<string>>(new Set());
  // Until the user changes it, the selection is re-seeded whenever the map, saved selection or capacity changes.
  const [edited, setEdited] = useState(false);
  const editedRef = useRef(false);
  const [fromFit, setFromFit] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  // The upsell's one impression waits for the open-time refetch, so it reports the settled opening selection.
  const [openSettled, setOpenSettled] = useState(false);
  const discardButtonRef = useRef<HTMLButtonElement>(null);
  const [seededMapKey, setSeededMapKey] = useState<string | null>(null);
  const openedAtRef = useRef<number | null>(null);
  const submitSnapshotRef = useRef({ selectedCount: 0, mapOverLimit: false, edited: false });
  const discardSnapshotRef = useRef({ selectedCount: 0, overLimit: false, mapOverLimit: false });
  // Defer review_opened until the open-time refetch settles so capacity is server-fresh, not the Infinity-cached preview.
  const openFetchStartedRef = useRef(false);
  const pendingReviewOpenRef = useRef<PendingReviewOpen | null>(null);
  const reviewOpenedFiredRef = useRef(false);
  const triggerRef = useRef<ReviewTrigger | null>(null);
  const dirtyRef = useRef(false);
  const savesPausedRef = useRef(false);
  const saveGenerationRef = useRef(0);
  // Saves run one after another: the endpoint replaces the whole selection, so an earlier save landing last would undo a later one.
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const pendingSavesRef = useRef(0);
  // Bumped when Scrape or Discard consumes the map, so its still-queued saves can't land on the next map.
  const consumedMapsRef = useRef(0);

  const previewQuery = useQuery<SiteMapPreview>({
    queryKey: [QueryKey.knowledgeBase.siteMapPreview.get],
    enabled: open,
  });
  const preview = previewQuery.data;
  const status: SiteMapStatus = preview?.status ?? 'none';
  const tree = (
    preview?.tree && 'path' in preview.tree ? preview.tree : null
  ) as UrlTreeNode | null;
  const totalUrls = preview?.total_urls ?? 0;
  // Empty map (dead/blocked domain, or a legacy ready+0 row): render the failure panel, not a dead-end picker.
  const mapEmpty = isSiteMapEmpty(status, totalUrls, preview?.error_message);
  const mapKey = tree ? siteMapKey(tree, totalUrls) : null;
  const seeded = seededMapKey !== null && seededMapKey === mapKey;
  const savedSelectionKey = JSON.stringify(preview?.selection ?? []);
  const resumeAll = siteMapResumeTarget() === 'all';

  const effectiveLimit = useMemo(() => {
    const limits = [preview?.scrape_limit, preview?.articles_left].filter(
      (limit): limit is number => typeof limit === 'number',
    );
    return limits.length ? Math.min(...limits) : NO_LIMIT;
  }, [preview?.scrape_limit, preview?.articles_left]);

  const selectedCount = tree ? countReviewSelection(tree, selection) : 0;
  const overLimit = selectedCount > effectiveLimit;
  const totalPages = tree?.count ?? 0;
  const mapOverLimit = totalPages > effectiveLimit;

  const dwellSeconds = () =>
    openedAtRef.current === null ? null : Math.round((Date.now() - openedAtRef.current) / 1000);

  // Emit a not-yet-fired review_opened so every terminal event has a matching open, even if the user acts before the refetch settles.
  const flushReviewOpened = () => {
    const pending = pendingReviewOpenRef.current;
    if (!pending) return;
    pendingReviewOpenRef.current = null;
    reviewOpenedFiredRef.current = true;
    setOpenSettled(true);
    const articlesLeft = preview?.articles_left ?? null;
    const mapOverLimitAtOpen = pending.totalPages > effectiveLimit;
    capture(PostHogEventEnum.SiteScrapeReviewOpened, {
      total_urls: pending.totalUrls,
      selected_count: pending.selectedCount,
      articles_left: articlesLeft,
      over_limit: pending.selectedCount > effectiveLimit,
      map_over_limit: mapOverLimitAtOpen,
      selection_source: pending.selectionSource,
      trigger: triggerRef.current,
      was_snoozed: pending.wasSnoozed,
      quality_warning: Boolean(preview?.homepage_reject_reason) || pending.totalUrls <= 1,
      homepage_reject_reason: preview?.homepage_reject_reason ?? null,
    });
    if (pending.selectionSource === 'fit') {
      capture(PostHogEventEnum.SiteScrapeSelectionChanged, {
        action: 'initial_fit',
        total_urls: pending.totalUrls,
        selected_count: pending.selectedCount,
        articles_left: articlesLeft,
        over_limit: pending.selectedCount > effectiveLimit,
        map_over_limit: mapOverLimitAtOpen,
      });
    }
  };

  const selectionChangedProps = (next: Set<string>, action: SelectionAction) => {
    const count = tree ? countReviewSelection(tree, next) : 0;
    return {
      action,
      total_urls: totalUrls,
      selected_count: count,
      articles_left: preview?.articles_left ?? null,
      over_limit: count > effectiveLimit,
      map_over_limit: mapOverLimit,
    };
  };

  const captureSelectionChanged = (props: Record<string, unknown>) => {
    flushReviewOpened();
    capture(PostHogEventEnum.SiteScrapeSelectionChanged, props);
  };

  const captureSelectionChangedRef = useRef(captureSelectionChanged);
  captureSelectionChangedRef.current = captureSelectionChanged;
  // A burst of toggles is one event; flush it before any other event so the funnel stays in order.
  const debouncedSelectionEvent = useMemo(
    () =>
      debounce(
        (props: Record<string, unknown>) => captureSelectionChangedRef.current(props),
        SELECTION_CHANGED_DEBOUNCE_MS,
      ),
    [],
  );
  const flushSelectionEvent = () => {
    debouncedSelectionEvent.flush();
  };
  const flushPendingEvents = () => {
    flushReviewOpened();
    flushSelectionEvent();
  };

  const selectionMutation = useMutation<unknown, Error, { selection: string[] }>({
    mutationKey: [QueryKey.knowledgeBase.siteMapPreview.selection],
    // Sync the cached selection (tree untouched) so a resumed map seeds the persisted selection, not the pre-persist one.
    onSuccess: (_data, variables) => {
      queryClient.setQueryData<SiteMapPreview>(
        [QueryKey.knowledgeBase.siteMapPreview.get],
        (old) => (old ? { ...old, selection: variables.selection } : old),
      );
    },
  });

  const sendSelection = (payload: string[]): Promise<void> => {
    const generation = saveGenerationRef.current;
    const consumedAtQueue = consumedMapsRef.current;
    pendingSavesRef.current += 1;
    const request = saveQueueRef.current
      .then(() =>
        consumedMapsRef.current === consumedAtQueue
          ? selectionMutation.mutateAsync({ selection: payload })
          : undefined,
      )
      .then(
        () => undefined,
        () => {
          if (saveGenerationRef.current === generation) dirtyRef.current = true;
        },
      )
      .finally(() => {
        pendingSavesRef.current -= 1;
      });
    saveQueueRef.current = request;
    return request;
  };

  const saveSelectionNow = () => {
    // Only a selection seeded for this map: after close it's reset to empty, and saving that would wipe the user's.
    if (!dirtyRef.current || savesPausedRef.current || !tree || status !== 'ready' || !seeded) {
      return;
    }
    dirtyRef.current = false;
    void sendSelection(toSavedSelection(selection, tree));
  };
  const saveSelectionRef = useRef(saveSelectionNow);
  saveSelectionRef.current = saveSelectionNow;
  const debouncedSave = useMemo(
    () => debounce(() => saveSelectionRef.current(), SELECTION_SAVE_DEBOUNCE_MS),
    [],
  );
  const flushSave = () => {
    debouncedSave.cancel();
    saveSelectionRef.current();
  };
  const dropPendingSaves = () => {
    debouncedSave.cancel();
    dirtyRef.current = false;
    saveGenerationRef.current += 1;
  };
  const resumeSaves = () => {
    savesPausedRef.current = false;
    if (dirtyRef.current) debouncedSave();
  };

  const scrapeMutation = useMutation<unknown, Error, { selection: string[] }>({
    mutationKey: [QueryKey.knowledgeBase.siteMapPreview.scrape],
    onSuccess: () => {
      consumedMapsRef.current += 1;
      savesPausedRef.current = false;
      dropPendingSaves();
      toast.success(t('tabs.website.review.scrape_started'));
      flushReviewOpened();
      capture(PostHogEventEnum.SiteScrapeSubmitted, {
        selected_count: submitSnapshotRef.current.selectedCount,
        total_urls: totalUrls,
        articles_left: preview?.articles_left ?? null,
        map_over_limit: submitSnapshotRef.current.mapOverLimit,
        selection_edited: submitSnapshotRef.current.edited,
        dwell_seconds: dwellSeconds(),
      });
      queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
      // The backend consumed (deleted) the map; refresh so every surface goes idle.
      queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.siteMapPreview.get] });
      queryClient.removeQueries({ queryKey: [QueryKey.knowledgeBase.siteMapPreview.children] });
      if (seededMapKey) localStorage.setItem(SITEMAP_CONSUMED_KEY, seededMapKey);
      // Seed the bottom-right progress widget so the batch tracks as a single
      // "Scraping X of N pages" bar (the backend meta article maps onto it).
      if (tree) addItems(rootDomain(tree), UploadItemTypeEnum.URL);
      // Drop the auto-open flags so the dock retires this now-handled map.
      clearSiteMapFlags();
      closeModal();
    },
    onError: (error) => {
      toast.error(
        get(error, 'response.data.errors.root[0].message') ||
          t('modals.upload_url.errors.import_failed'),
      );
      // Capacity may have shifted under us (another tab/device); re-pull the truth.
      queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.siteMapPreview.get] });
      resumeSaves();
    },
  });

  const discardMutation = useMutation<unknown, Error, void>({
    mutationKey: [QueryKey.knowledgeBase.siteMapPreview.delete],
    onSuccess: () => {
      consumedMapsRef.current += 1;
      savesPausedRef.current = false;
      dropPendingSaves();
      forgetSiteMap();
      queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.siteMapPreview.get] });
      queryClient.removeQueries({ queryKey: [QueryKey.knowledgeBase.siteMapPreview.children] });
      toast.success(t('tabs.website.review.discard_toast'));
      flushReviewOpened();
      capture(PostHogEventEnum.SiteScrapeDiscarded, {
        source: 'modal',
        selected_count: discardSnapshotRef.current.selectedCount,
        total_urls: totalUrls,
        articles_left: preview?.articles_left ?? null,
        over_limit: discardSnapshotRef.current.overLimit,
        map_over_limit: discardSnapshotRef.current.mapOverLimit,
        dwell_seconds: dwellSeconds(),
      });
      closeModal();
    },
    onError: (error) => {
      toast.error(
        get(error, 'response.data.errors.root[0].message') ||
          t('modals.upload_url.errors.import_failed'),
      );
      resumeSaves();
    },
  });

  // Re-pull the preview whenever the modal opens: articles_left changes out-of-band
  // (uploads elsewhere, plan upgrades), and staleTime is Infinity app-wide.
  useEffect(() => {
    if (!open) return;
    openFetchStartedRef.current = false;
    pendingReviewOpenRef.current = null;
    reviewOpenedFiredRef.current = false;
    triggerRef.current = readTrigger();
    dirtyRef.current = false;
    saveGenerationRef.current += 1;
    let cancelled = false;
    const refetch = () => {
      if (cancelled) return;
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.knowledgeBase.siteMapPreview.get],
      });
    };
    // Wait out every queued save from the last close, or a fast reopen would load the selection they replace.
    if (pendingSavesRef.current > 0) void saveQueueRef.current.then(refetch);
    else refetch();
    return () => {
      cancelled = true;
    };
  }, [open, queryClient]);

  // Not keyed on `tree`: lazily grafted children must not re-seed the selection.
  useEffect(() => {
    // An empty map renders the failure panel; don't seed a selection or queue review_opened for it.
    if (!(open && status === 'ready' && tree && mapKey) || totalUrls === 0) return;
    const isNewMap = seededMapKey !== mapKey;
    if (!isNewMap && editedRef.current) return;
    if (isNewMap) {
      flushSelectionEvent();
      dropPendingSaves();
      editedRef.current = false;
      setEdited(false);
      openedAtRef.current = Date.now();
    }
    const seed = seedSelection(tree, preview?.selection ?? [], effectiveLimit, resumeAll);
    setSelection(seed.selection);
    setFromFit(seed.source === 'fit');
    setSeededMapKey(mapKey);
    // Hold the initial selection; the capture effect below fills in server-fresh capacity once a fetch settles.
    if (!reviewOpenedFiredRef.current) {
      pendingReviewOpenRef.current = {
        totalUrls,
        totalPages: tree.count,
        selectedCount: countReviewSelection(tree, seed.selection),
        selectionSource: seed.source,
        wasSnoozed: siteMapSnoozeState(mapKey) !== 'none',
      };
    }
  }, [open, status, mapKey, savedSelectionKey, effectiveLimit, resumeAll]);

  // Fire review_opened once per open, after the open-time refetch settles, so capacity comes from the server not the cache.
  useEffect(() => {
    if (!open) return;
    if (previewQuery.isFetching) {
      openFetchStartedRef.current = true;
      return;
    }
    if (openFetchStartedRef.current) flushReviewOpened();
  }, [open, previewQuery.isFetching]);

  // However the modal closed (incl. another modal replacing it), keep the last toggles and their event.
  useEffect(() => {
    if (open) return;
    flushSelectionEvent();
    flushSave();
    setSelection(new Set());
    editedRef.current = false;
    setEdited(false);
    setFromFit(false);
    setDiscardConfirmOpen(false);
    setOpenSettled(false);
    setSeededMapKey(null);
    openedAtRef.current = null;
    openFetchStartedRef.current = false;
    pendingReviewOpenRef.current = null;
  }, [open]);

  useEffect(
    () => () => {
      debouncedSelectionEvent.flush();
      debouncedSave.cancel();
      saveSelectionRef.current();
    },
    [debouncedSelectionEvent, debouncedSave],
  );

  // Meter is the KB article allowance: existing articles pre-fill the bar, the
  // selection stacks on top, and "remaining" is the unfilled track.
  const articleLimitRaw = getConfig('articles_limit');
  const articleLimit =
    typeof articleLimitRaw === 'number' && articleLimitRaw > 0 ? articleLimitRaw : null;
  const showCapacity = effectiveLimit !== NO_LIMIT && articleLimit !== null;
  const usedCount = articleLimit !== null ? Math.max(0, articleLimit - effectiveLimit) : 0;
  const projectedUsed = usedCount + selectedCount;
  const usedPct = articleLimit !== null ? Math.min(100, (usedCount / articleLimit) * 100) : 0;
  const selectedPct =
    articleLimit !== null ? Math.min(100, (selectedCount / articleLimit) * 100) : 0;
  const selectedSegmentPct = Math.min(selectedPct, 100 - usedPct);

  const busy = scrapeMutation.isPending || discardMutation.isPending;
  const canTrim = overLimit && effectiveLimit !== NO_LIMIT && effectiveLimit > 0;
  // aria-disabled, not disabled: a disabled button leaves the tab order, taking its reason with it.
  const scrapeBlocked = selectedCount === 0 || overLimit;
  const blockedReasonId = useId();
  const showFitNote = fromFit && !overLimit;
  // Mount the upsell only once the selection is seeded, so its one impression reports the real selection.
  const showUpgrade = mapOverLimit && seeded && openSettled;

  // Every user edit: persist it (debounced), drop a pending resume-everything, and record it.
  const changeSelection = (next: Set<string>, action: Exclude<SelectionAction, 'initial_fit'>) => {
    if (!tree) return;
    if (action !== 'toggle') flushSelectionEvent();
    setSelection(next);
    editedRef.current = true;
    setEdited(true);
    setFromFit(action === 'fit');
    if (siteMapResumeTarget() === 'all') flagSiteMapResume('selection');
    dirtyRef.current = true;
    debouncedSave();
    const props = selectionChangedProps(next, action);
    if (action === 'toggle') debouncedSelectionEvent(props);
    else captureSelectionChanged(props);
  };

  const handleScrape = () => {
    if (selectedCount === 0 || overLimit || busy) return;
    flushSelectionEvent();
    submitSnapshotRef.current = { selectedCount, mapOverLimit, edited };
    savesPausedRef.current = true;
    scrapeMutation.mutate({ selection: Array.from(selection) });
  };

  const handleDeselectAll = () => changeSelection(clearSelection(), 'deselect_all');

  const handleTrimToPlan = () => {
    if (!tree || effectiveLimit === NO_LIMIT || effectiveLimit <= 0) return;
    changeSelection(trimToLimit(tree, effectiveLimit, selection), 'fit');
  };

  const discardNow = () => {
    if (discardMutation.isPending) return;
    flushSelectionEvent();
    discardSnapshotRef.current = { selectedCount, overLimit, mapOverLimit };
    savesPausedRef.current = true;
    discardMutation.mutate();
  };

  const handleConfirmDiscard = () => {
    setDiscardConfirmOpen(false);
    discardNow();
  };

  // Close without scraping or discarding (Cancel, Esc, overlay): the map stays
  // resumable from the dock, but the walk-away itself is a funnel signal.
  const closeWithoutAction = () => {
    flushReviewOpened();
    flushSelectionEvent();
    flushSave();
    if (openedAtRef.current !== null) {
      capture(PostHogEventEnum.SiteScrapeReviewClosed, {
        total_urls: totalUrls,
        selected_count: selectedCount,
        articles_left: preview?.articles_left ?? null,
        over_limit: overLimit,
        map_over_limit: mapOverLimit,
        selection_edited: edited,
        dwell_seconds: dwellSeconds(),
      });
      openedAtRef.current = null;
    }
    closeModal();
  };

  // Persist the selection + flag a post-checkout resume before the Stripe redirect.
  const persistSelectionAndFlag = async () => {
    flushPendingEvents();
    debouncedSave.cancel();
    dirtyRef.current = false;
    // best-effort: the resume flag still goes up so the modal reopens after checkout
    if (tree) await sendSelection(toSavedSelection(selection, tree));
    flagSiteMapResume(overLimit ? 'selection' : 'all');
    clearSiteMapSnooze();
  };

  // Paid plans upgrade via the subscription page; keep the selection + resume
  // flag so the dock re-offers this review once the plan change lands.
  const handleManagePlan = async () => {
    await persistSelectionAndFlag();
    closeModal();
  };

  const handleChildrenLoaded = (path: string, batch: UrlTreeNode[]) => {
    queryClient.setQueryData<SiteMapPreview>([QueryKey.knowledgeBase.siteMapPreview.get], (old) =>
      old && old.tree && 'path' in old.tree
        ? { ...old, tree: mergeChildren(old.tree as UrlTreeNode, path, batch) }
        : old,
    );
  };

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) closeWithoutAction();
      }}
    >
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              />
            </DialogPrimitive.Overlay>
            {/* Plain centering layer; the card itself is the Radix Content. */}
            <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4">
              <DialogPrimitive.Content
                asChild
                forceMount
                onOpenAutoFocus={(event) => event.preventDefault()}
              >
                <motion.div
                  // Grows out of the bottom-right dock: starts small + offset
                  // toward the dock, settles into the centered card.
                  className="pointer-events-auto relative flex h-[88vh] max-h-[720px] w-full max-w-2xl flex-col gap-4 rounded-2xl border border-border bg-card p-6 text-card-foreground shadow-2xl focus:outline-none"
                  initial={{ opacity: 0, scale: 0.85, x: 80, y: 80 }}
                  animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
                  exit={{ opacity: 0, scale: 0.92, x: 60, y: 60 }}
                  transition={{ type: 'spring', stiffness: 260, damping: 26, mass: 0.9 }}
                  style={{ transformOrigin: 'bottom right' }}
                  data-testid="site-scrape-review-modal"
                >
                  <div className="flex flex-col gap-1">
                    <DialogPrimitive.Title className="flex items-center gap-2 text-lg font-semibold text-foreground">
                      <span className="flex size-7 items-center justify-center rounded-lg bg-muted">
                        <Globe className="size-4 text-foreground" />
                      </span>
                      {t('tabs.website.review.title')}
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="text-sm text-muted-foreground">
                      {t('tabs.website.review.description')}
                    </DialogPrimitive.Description>
                  </div>

                  {status === 'failed' || mapEmpty ? (
                    <div
                      className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground"
                      data-testid="site-scrape-review-modal-failed"
                    >
                      <AlertTriangle className="size-6 text-destructive" />
                      <span className="max-w-sm text-center text-sm">
                        {t(
                          mapEmpty
                            ? 'tabs.website.review.map_empty'
                            : 'tabs.website.review.map_failed',
                        )}
                      </span>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={discardNow}
                        disabled={busy}
                        data-testid="site-scrape-review-failed-discard"
                      >
                        {discardMutation.isPending ? (
                          <Spinner className="size-4" />
                        ) : (
                          t('tabs.website.review.dismiss')
                        )}
                      </Button>
                    </div>
                  ) : status !== 'ready' || !tree ? (
                    <div
                      className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground"
                      data-testid="site-scrape-review-modal-loading"
                    >
                      <Spinner className="size-6" />
                      <span className="text-sm">{t('tabs.website.review.mapping')}</span>
                    </div>
                  ) : (
                    <>
                      <div
                        className={cn(
                          'flex shrink-0 flex-col gap-2.5 rounded-xl px-4 py-3 transition-colors',
                          overLimit
                            ? 'border border-destructive/30 bg-destructive-muted/40'
                            : 'bg-muted',
                        )}
                      >
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-foreground" data-testid="site-scrape-review-found">
                            {t('tabs.website.review.found', { count: preview?.total_urls ?? 0 })}
                          </span>
                          {showCapacity && (
                            <span
                              className={cn(
                                'tabular-nums text-muted-foreground',
                                overLimit && 'font-semibold text-foreground',
                              )}
                              data-testid="site-scrape-review-limit"
                            >
                              {t('tabs.website.review.articles_used', {
                                used: projectedUsed,
                                limit: articleLimit ?? 0,
                              })}
                            </span>
                          )}
                        </div>
                        {showCapacity && (
                          <div
                            className="relative h-2 w-full overflow-hidden rounded-full bg-muted-foreground/15"
                            data-testid="site-scrape-review-progress"
                          >
                            <div
                              className={cn(
                                'absolute inset-y-0 left-0',
                                overLimit ? 'bg-destructive' : 'bg-foreground',
                              )}
                              style={{ width: `${usedPct}%` }}
                            />
                            <div
                              className={cn(
                                'absolute inset-y-0 transition-all duration-300',
                                overLimit ? 'bg-destructive' : 'bg-foreground',
                              )}
                              style={{ left: `${usedPct}%`, width: `${selectedSegmentPct}%` }}
                            />
                          </div>
                        )}
                        <div
                          className={cn(
                            'flex flex-wrap items-center justify-between gap-2',
                            !overLimit && 'sr-only',
                          )}
                          data-testid="site-scrape-review-over-limit-note"
                        >
                          <p
                            role="status"
                            aria-live="polite"
                            className="text-sm font-medium text-foreground"
                            data-testid="site-scrape-review-over-limit-status"
                          >
                            {overLimit &&
                              t(
                                canTrim
                                  ? 'tabs.website.review.over_limit'
                                  : 'tabs.website.review.over_limit_no_capacity',
                              )}
                          </p>
                          {canTrim && (
                            <Button
                              variant="dark"
                              size="sm"
                              onClick={handleTrimToPlan}
                              disabled={busy}
                              data-testid="site-scrape-review-trim-to-plan"
                            >
                              {t('tabs.website.review.trim_to_plan', { limit: effectiveLimit })}
                            </Button>
                          )}
                        </div>
                        {showFitNote && (
                          <p
                            className="text-xs text-muted-foreground"
                            data-testid="site-scrape-review-fit-note"
                          >
                            {t('tabs.website.review.fit_note')}
                          </p>
                        )}
                      </div>

                      <div
                        className="flex shrink-0 flex-wrap items-center gap-2"
                        data-testid="site-scrape-review-bulk-actions"
                      >
                        <Button
                          variant="secondary"
                          size="xs"
                          onClick={handleDeselectAll}
                          disabled={selectedCount === 0 || busy}
                          data-testid="site-scrape-review-deselect-all"
                        >
                          {t('tabs.website.review.deselect_all')}
                        </Button>
                      </div>

                      <p
                        className="shrink-0 px-1 text-xs text-muted-foreground"
                        data-testid="site-scrape-review-unavailable-note"
                      >
                        {t('tabs.website.review.unavailable_note')}
                      </p>

                      {(preview?.homepage_reject_reason || (preview?.total_urls ?? 0) <= 1) && (
                        <Alert
                          variant="warning"
                          className="shrink-0"
                          data-testid="site-scrape-review-quality-warning"
                        >
                          <AlertTitle>{t('tabs.website.review.low_quality.title')}</AlertTitle>
                          <AlertDescription>
                            {preview?.homepage_reject_reason
                              ? t(lowQualityModalKey(preview.homepage_reject_reason))
                              : t('tabs.website.review.low_quality.few_pages')}
                          </AlertDescription>
                        </Alert>
                      )}

                      <div
                        className="-mx-1 flex min-h-0 flex-1 flex-col overflow-y-auto pl-1 pr-3"
                        data-testid="site-scrape-review-tree"
                      >
                        <UrlTree
                          node={tree}
                          selection={selection}
                          onToggle={(path) =>
                            changeSelection(toggleSelection(selection, path), 'toggle')
                          }
                          onChildrenLoaded={handleChildrenLoaded}
                        />
                      </div>

                      {showUpgrade && (
                        <SiteScrapeUpgrade
                          dense
                          selectedCount={selectedCount}
                          targetCount={overLimit ? selectedCount : totalPages}
                          selectionFits={!overLimit}
                          usedCount={usedCount}
                          currentLimit={articleLimit}
                          planLimit={effectiveLimit === NO_LIMIT ? null : effectiveLimit}
                          isPaidPlan={isPaidPlan}
                          onBeforeUpgradeClick={flushPendingEvents}
                          onBeforeCheckout={persistSelectionAndFlag}
                          onManagePlan={handleManagePlan}
                          discountApplied={discountClaimable && !isPaidPlan}
                          discountPercentOff={ONBOARDING_DISCOUNT_PERCENT_OFF}
                          promoCode={discountClaimable ? discount?.code : undefined}
                        />
                      )}

                      <div className="flex w-full items-center justify-between gap-3 border-t border-border pt-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          ref={discardButtonRef}
                          onClick={() => setDiscardConfirmOpen(true)}
                          disabled={busy}
                          className="gap-1.5 px-2 text-muted-foreground hover:text-destructive"
                          data-testid="site-scrape-review-discard-button"
                        >
                          {discardMutation.isPending ? (
                            <Spinner className="size-4" />
                          ) : (
                            <Trash2 className="size-4" />
                          )}
                          {t('tabs.website.review.discard')}
                        </Button>
                        <div className="ml-auto flex items-center gap-3">
                          <span
                            id={blockedReasonId}
                            className={cn(
                              'sr-only text-sm sm:not-sr-only',
                              overLimit ? 'text-foreground' : 'text-muted-foreground',
                            )}
                            data-testid="site-scrape-review-status"
                          >
                            {overLimit
                              ? t('tabs.website.review.over_limit_by', {
                                  count: selectedCount - effectiveLimit,
                                })
                              : t('tabs.website.review.selected', { count: selectedCount })}
                          </span>
                          <Button
                            variant="secondary"
                            onClick={closeWithoutAction}
                            disabled={busy}
                            className="shrink-0"
                            data-testid="site-scrape-review-cancel-button"
                          >
                            {t('tabs.website.review.cancel')}
                          </Button>
                          <Button
                            variant="dark"
                            onClick={handleScrape}
                            disabled={busy}
                            aria-disabled={scrapeBlocked || undefined}
                            aria-describedby={scrapeBlocked ? blockedReasonId : undefined}
                            className="shrink-0 aria-disabled:pointer-events-none aria-disabled:bg-foreground/20 aria-disabled:text-background/80 aria-disabled:opacity-50 aria-disabled:focus-visible:bg-foreground/20"
                            data-testid="site-scrape-review-scrape-button"
                          >
                            {scrapeMutation.isPending && <Spinner className="size-4" />}
                            {t('tabs.website.review.scrape_button', { count: selectedCount })}
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                  <SiteScrapeDiscardConfirm
                    open={discardConfirmOpen}
                    onOpenChange={setDiscardConfirmOpen}
                    onConfirm={handleConfirmDiscard}
                    returnFocusRef={discardButtonRef}
                  />
                </motion.div>
              </DialogPrimitive.Content>
            </div>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
};
